import { IAttachment } from "@base/modules/appointment/appointment.interface";
import { ILifeInsurance, ILifeInsuranceProvider } from "./lifeInsurance.interface";

export interface IProviderGroup {
  /** The insurer, e.g. "ABIC" - what the patient picks first. */
  provider: string;
  /** Its plans (customer groups), in the order the server listed them. */
  plans: ILifeInsuranceProvider[];
}

/** Plans arrive flat; they are grouped by `provider`, keeping the order each insurer first appears in. */
export const groupProviders = (plans: ILifeInsuranceProvider[]): IProviderGroup[] =>
  plans.reduce<IProviderGroup[]>((groups, plan) => {
    const provider = plan.provider ?? "";
    const group = groups.find((item) => item.provider === provider);

    if (group) {
      group.plans.push(plan);
    } else {
      groups.push({ provider, plans: [plan] });
    }

    return groups;
  }, []);

export interface ILifeInsuranceDraft {
  plan?: ILifeInsuranceProvider;
  /** `YYYY-MM-DD` */
  startDate?: string;
  /** `YYYY-MM-DD` */
  endDate?: string;
  description?: string;
}

/**
 * The save body, as mobile sends it. Both `name` and `provider` carry the plan's `name` - not the
 * insurer - and an edit keeps the record's own plan, since plan and insurer cannot be changed.
 */
export const buildLifeInsuranceBody = (draft: ILifeInsuranceDraft, patientRecordCode: string, existing?: ILifeInsurance): ILifeInsurance => {
  const plan = existing ? existing.health_insurance : draft.plan;

  return {
    id: existing?.id ?? 0,
    name: plan?.name,
    provider: plan?.name,
    description: draft.description?.trim(),
    start_date: draft.startDate,
    end_date: draft.endDate,
    patient_record_code: patientRecordCode,
    health_insurance: plan,
    can_edit: false,
    show_card: false,
  };
};

/** The photos an edit removed, by `object_name` - each has to be deleted on its own. */
export const getRemovedImageNames = (before: IAttachment[] = [], keptLinks: string[]) =>
  before.filter((image) => image.link && !keptLinks.includes(image.link)).map((image) => String(image.object_name));
